import React, { useEffect, useState } from 'react';
import { apiFetch } from '../lib/apiFetch';
import { StandupResumen, ApiResponse, ESTADO_LABELS } from '../types';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:4000/api';

export const StandupPanel: React.FC = () => {
    const [data, setData] = useState<StandupResumen | null>(null);
    const [loading, setLoading] = useState(false);

    const fetchData = async () => {
        setLoading(true);
        try {
            const res = await apiFetch(`${API_BASE}/tareas/standup`);
            const json: ApiResponse<StandupResumen> = await res.json();
            if (json.success && json.data) setData(json.data);
        } catch (e) {
            console.error(e);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchData(); }, []);

    if (!data) {
        return <div className="text-center text-slate-400 py-8">{loading ? 'Cargando...' : 'Sin datos'}</div>;
    }

    return (
        <div className="bg-white border border-slate-200 rounded-lg p-5 shadow-sm">
            <div className="flex items-center justify-between mb-4">
                <div>
                    <h3 className="text-base font-semibold text-slate-800">📅 Standup Diario</h3>
                    <p className="text-xs text-slate-500">
                        Recorrido derecha → izquierda. ¿Qué impide que cada item fluya?
                    </p>
                </div>
                <button
                    onClick={fetchData}
                    disabled={loading}
                    className="px-3 py-1 text-xs bg-slate-100 hover:bg-slate-200 text-slate-700 rounded"
                >
                    {loading ? '⟳' : '↻'} Refrescar
                </button>
            </div>

            <Section title="✅ Producidos recientemente (últimas 24h)" items={data.items_producidos_recientes.map(i => ({
                id: i.id, text: i.titulo, sub: i.empresa, color: 'emerald'
            }))} emptyText="Nada nuevo en producción" />

            <Section title="🧪 Testing estancados (≥1 día)" items={data.testing_estancados.map(i => ({
                id: i.id, text: i.titulo, sub: `${i.dias}d · ${i.empresa}`, color: 'purple'
            }))} emptyText="Sin estancados en Testing" />

            <Section title="🔄 En Progreso estancados (≥5 días)" items={data.en_progreso_estancados.map(i => ({
                id: i.id, text: i.titulo, sub: `${i.dias}d · ${i.empresa}${i.responsable ? ` · 👤 ${i.responsable}` : ''}`, color: 'amber'
            }))} emptyText="Sin items estancados en En Progreso" />

            <Section title="⏳ Esperando Cliente" items={data.esperando_cliente.map(i => ({
                id: i.id, text: i.titulo, sub: `${i.dias}d · ${i.empresa}`, color: 'orange'
            }))} emptyText="Sin items esperando cliente" />

            {data.bloqueos_nuevos_hoy > 0 && (
                <div className="mt-3 p-2 bg-red-50 border border-red-200 rounded text-sm text-red-700">
                    🚫 <strong>{data.bloqueos_nuevos_hoy}</strong> bloqueo(s) nuevo(s) hoy.
                </div>
            )}
        </div>
    );
};

interface SectionItem {
    id: number;
    text: string;
    sub: string;
    color: 'emerald' | 'purple' | 'amber' | 'orange' | 'red' | 'slate';
}

const Section: React.FC<{ title: string; items: SectionItem[]; emptyText: string }> = ({ title, items, emptyText }) => (
    <div className="mb-4">
        <h4 className="text-xs font-semibold text-slate-600 uppercase tracking-wide mb-2">{title}</h4>
        {items.length === 0 ? (
            <div className="text-xs text-slate-400 italic py-1">— {emptyText}</div>
        ) : (
            <div className="space-y-1">
                {items.map(item => (
                    <div key={item.id} className="flex items-baseline gap-2 text-sm">
                        <span className="font-mono text-xs text-slate-500 w-8">#{item.id}</span>
                        <span className="text-slate-800 truncate flex-1">{item.text}</span>
                        <span className="text-xs text-slate-500">{item.sub}</span>
                    </div>
                ))}
            </div>
        )}
    </div>
);
